import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Filter, AlertTriangle, Upload, Clock, FlaskConical, Award, Syringe, Heart, FolderOpen, Download, Trash2, History, Share2, ShieldCheck, ShieldAlert, PenLine } from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type VaultRecord, type VaultProvenance } from '../api';
import type { TimelineEvent } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import InstitutionShareModal from '../components/InstitutionShareModal';
import Spinner from '../../../components/ui/Spinner';

/**
 * The Health Vault, read from the vault.
 *
 * This page used to read `GET /health-vault/documents` — which is ONLY the
 * patient's own uploads — and pad the rest out of the device's own store:
 * prescriptions, lab and imaging reports, appointments, claims and ambulance
 * requests, every one of them hardcoded `PENDING_NEOVERIFY`. Three things were
 * wrong with that. The records were device copies, so a reinstall lost them and
 * another phone never had them. Documents a provider had genuinely registered
 * read back as "pending". And the list was two rows of eighteen.
 *
 * `GET /vault` is the whole vault: prescriptions, lab and imaging reports,
 * certificates, vaccinations, insurance documents, pharmacy invoices, ambulance
 * trip reports and the uploads, each row carrying its real issuer, document
 * number and what the registry says about it.
 */

/**
 * The server's own filter buckets. Not this page's invention: `/vault` files
 * every record into one of these and answers `?type=` in the same words.
 *
 * The list here used to be fifteen chips of its own devising — DISCHARGE_
 * SUMMARY, ECC_EMERGENCY_RECORD, BLOOD_BANK_RECORD, NEOVERIFY_DOCUMENT and the
 * rest — and no endpoint produced a single one of them. A real uploaded report
 * comes back as `Self-Uploaded`, matched no chip, and vanished under every
 * filter but All.
 */
const CATEGORIES: { key: string; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'Prescriptions', label: 'Prescriptions' },
  { key: 'Lab', label: 'Lab' },
  { key: 'Imaging', label: 'Imaging' },
  { key: 'Vaccination', label: 'Vaccination' },
  { key: 'Certificates', label: 'Certificates' },
  { key: 'Insurance', label: 'Insurance' },
  { key: 'Invoices', label: 'Invoices' },
  { key: 'Ambulance', label: 'Ambulance' },
  { key: 'Self-Uploaded', label: 'Uploaded' },
];

/** The categories `POST /health-vault/documents` accepts, with plain labels. */
const UPLOAD_CATEGORIES: { key: string; label: string }[] = [
  { key: 'REPORT', label: 'Report' },
  { key: 'PRESCRIPTION', label: 'Prescription' },
  { key: 'SCAN', label: 'Scan' },
  { key: 'INSURANCE', label: 'Insurance' },
  { key: 'BILL', label: 'Bill' },
  { key: 'VACCINATION', label: 'Vaccination' },
  { key: 'OTHER', label: 'Other' },
];

/**
 * The one provenance signal a card shows, in the registry's own words.
 *
 * SELF_ENTERED is not a lesser VERIFIED and is not coloured like a warning:
 * the patient put it there themselves, which is a fact about the document,
 * not a fault in it.
 */
const VERIFICATION: Record<string, { label: string; fg: string; bg: string; border: string; Icon: typeof ShieldCheck }> = {
  VERIFIED: { label: 'Verified', fg: '#047857', bg: '#ECFDF5', border: '#A7F3D0', Icon: ShieldCheck },
  INSURER_CONFIRMED: { label: 'Confirmed by insurer', fg: '#047857', bg: '#ECFDF5', border: '#A7F3D0', Icon: ShieldCheck },
  SELF_ENTERED: { label: 'Added by you', fg: '#475569', bg: '#F8FAFC', border: '#E2E8F0', Icon: PenLine },
  UNREGISTERED: { label: 'Not registered', fg: '#B45309', bg: '#FFFBEB', border: '#FDE68A', Icon: ShieldAlert },
  REVOKED: { label: 'Withdrawn by issuer', fg: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', Icon: ShieldAlert },
  SUPERSEDED: { label: 'Replaced by issuer', fg: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', Icon: ShieldAlert },
  // The registry computes these two at verification time rather than storing
  // them, so they do not reach this list today. Named anyway: an unnamed
  // verdict falls through to a neutral grey chip, and ALTERED — the document
  // no longer matches its registered digest — must never render as quietly as
  // a pass.
  ALTERED: { label: 'Altered', fg: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', Icon: ShieldAlert },
  UNCHECKABLE: { label: 'Could not be checked', fg: '#B45309', bg: '#FFFBEB', border: '#FDE68A', Icon: ShieldAlert },
};

/**
 * A document the patient added themselves, and therefore the only kind they
 * may download as a file or remove. The server calls this source
 * `vault-upload`; everything else in the vault is somebody else's record of
 * what they did, and is rendered from the registry instead.
 */
const isSelfUpload = (r: VaultRecord) => r.source === 'vault-upload';

const fmtDate = (v: string | null) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtWhen = (v: string) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * An extension matching what the server actually sent.
 *
 * An upload keeps its own type — a scan saved as `report` with no extension
 * opens in nothing.
 */
const EXT_FOR: Record<string, string> = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
};

/** Save a blob the browser was handed, under a name a person can find again. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function HealthVault() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<VaultRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('ALL');
  const [nhcId, setNhcId] = useState('');
  const [shareItem, setShareItem] = useState<{ id: string; title: string } | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('REPORT');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<VaultRecord | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    patientApi.vault()
      .then((v) => setRecords(v ?? []))
      .catch(() => setError('Your documents could not be loaded from the server. Nothing is missing from your account — this page could not read it.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    patientApi.getProfile()
      .then((p) => { if (alive) setNhcId(p.nhcId); })
      .catch(() => { /* the badge simply does not appear */ });
    // The real timeline, not the device's copy of one.
    patientApi.getTimeline({ limit: 4 })
      .then((t) => { if (alive) setTimeline(t.events ?? []); })
      .catch(() => { if (alive) setTimeline([]); });
    return () => { alive = false; };
  }, []);

  const handleUpload = async (f: File) => {
    setUploading(true);
    setNotice(null);
    try {
      await patientApi.uploadHealthVaultDocument({
        file: f,
        title: f.name.replace(/\.[^.]+$/, ''),
        // What the patient said it is. It used to be hardcoded 'OTHER', so
        // every upload filed itself under the one bucket nobody looks in.
        category: uploadCategory,
      });
      // Re-read rather than splicing the reply in: the row this becomes in the
      // vault is the server's, with its own id and verification state.
      load();
      setNotice({ kind: 'ok', text: `"${f.name}" added to your Health Vault.` });
    } catch {
      setNotice({ kind: 'error', text: `"${f.name}" could NOT be uploaded — the server rejected it or is unreachable. Only PDF/JPG/PNG up to 5MB are accepted.` });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /**
   * Hand the patient the document.
   *
   * An upload has a real file on the server. Everything else is rows in a
   * database, so there is nothing to hand over until it is rendered — which is
   * what the NeoVerify PDF is. A record with neither is said to have neither.
   */
  const handleDownload = async (r: VaultRecord) => {
    setBusyId(r.id);
    setNotice(null);
    try {
      if (isSelfUpload(r)) {
        const blob = await patientApi.getHealthVaultFileBlob(r.id);
        const base = (r.title || 'document').replace(/[^\w.-]+/g, '-');
        const ext = EXT_FOR[blob.type] ?? '';
        saveBlob(blob, base.toLowerCase().endsWith(ext) ? base : base + ext);
      } else if (r.neoVerifyId) {
        saveBlob(await patientApi.neoVerifyPdf(r.neoVerifyId), `${r.documentNumber || r.neoVerifyId}.pdf`);
      } else {
        setNotice({ kind: 'error', text: `"${r.title}" was never registered with NeoVerify, so there is no copy to download. Ask ${r.provider || 'the issuer'} for one.` });
      }
    } catch {
      setNotice({ kind: 'error', text: `"${r.title}" could not be downloaded.` });
    } finally {
      setBusyId(null);
    }
  };

  /** Only a document the patient added themselves is theirs to remove. */
  const handleDelete = async (r: VaultRecord) => {
    if (!confirm(`Remove "${r.title}" from your Health Vault? This cannot be undone.`)) return;
    setBusyId(r.id);
    setNotice(null);
    try {
      await patientApi.deleteHealthVaultDocument(r.id);
      setRecords((prev) => prev.filter((x) => x.id !== r.id));
      setNotice({ kind: 'ok', text: `"${r.title}" removed.` });
    } catch {
      setNotice({ kind: 'error', text: `"${r.title}" could not be removed.` });
    } finally {
      setBusyId(null);
    }
  };

  const filtered = filter === 'ALL' ? records : records.filter((r) => r.category === filter);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" style={govPageContainer()}>
      {error && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <Archive className="w-6 h-6 text-teal-600" /> Health Vault
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">All documents linked to your NeoHealthCard ID</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {nhcId && <NHCIdBadge nhcId={nhcId} />}
          {/* What the document is, chosen before it is sent — the server files
              it under that, and the filter above finds it there. */}
          <select
            value={uploadCategory}
            onChange={(e) => setUploadCategory(e.target.value)}
            disabled={uploading}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 bg-white"
            aria-label="What kind of document are you adding?"
          >
            {UPLOAD_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <label className={`text-xs text-white ${uploading ? 'bg-teal-400' : 'bg-teal-600 hover:bg-teal-700'} px-2.5 py-1 rounded-lg cursor-pointer flex items-center gap-1`}>
            <Upload className="w-3 h-3" /> {uploading ? 'Uploading…' : 'Upload'}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={uploading} onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }} />
          </label>
        </div>
      </div>

      {/* ── Vault Sections ── */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 12 }}>Inside Health Vault</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {[
            { title: 'Timeline', subtitle: 'Your healthcare journey', icon: Clock, color: T.teal, route: '/patient/timeline' },
            { title: 'Medical Documents', subtitle: 'Prescriptions, summaries, uploads', icon: FolderOpen, color: T.navy, route: '/patient/documents' },
            { title: 'Lab Reports', subtitle: 'Test results & history', icon: FlaskConical, color: '#7C3AED', route: '/patient/lab-reports' },
            { title: 'Certificates', subtitle: 'Medical certificates', icon: Award, color: T.warning, route: '/patient/certificates' },
            { title: 'Vaccinations', subtitle: 'Immunization records', icon: Syringe, color: T.success, route: '/patient/vaccinations' },
            { title: 'Emergency Records', subtitle: 'Emergency & ambulance logs', icon: Heart, color: T.danger, route: '/patient/emergency-records' },
          ].map(s => (
            <div key={s.title} onClick={() => navigate(s.route)} style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
              padding: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: T.shadow, transition: 'box-shadow 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = `${s.color}40`; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = T.shadow; e.currentTarget.style.borderColor = T.border; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${s.color}0C`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <s.icon style={{ width: 18, height: 18, color: s.color }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font, lineHeight: 1.2 }}>{s.title}</div>
                <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>{s.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Recent Timeline Preview — the account's, not this device's ── */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: 20, boxShadow: T.shadow,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock style={{ width: 16, height: 16, color: T.teal }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font }}>Recent Timeline</span>
          </div>
          <span onClick={() => navigate('/patient/timeline')} style={{ fontSize: 12, fontWeight: 600, color: T.teal, fontFamily: T.font, cursor: 'pointer' }}>View full timeline →</span>
        </div>
        {timeline.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', paddingLeft: 16 }}>
            <div style={{ position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, background: T.border, borderRadius: 1 }} />
            {timeline.slice(0, 4).map(event => (
              <div key={event.id} style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{
                  position: 'absolute', left: -14, top: 3, width: 10, height: 10, borderRadius: 5,
                  background: `${T.teal}20`, border: `2px solid ${T.teal}`, zIndex: 1,
                }} />
                <div style={{ flex: 1, marginLeft: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: T.font, lineHeight: 1.3 }}>{event.title}</div>
                  {event.description && (
                    <div style={{ fontSize: 10.5, color: T.textSec, fontFamily: T.font, marginTop: 1 }}>{event.description}</div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {fmtDate(event.eventDate)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 12, color: T.textSec, fontFamily: T.font }}>
            No recent activity yet — your healthcare events will appear here.
          </div>
        )}
      </div>

      {/* ── Document Vault ── */}
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font }}>All Documents</div>

      {/* Filters — the server's own buckets, each one a chip that can match */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="w-4 h-4 text-gray-400 shrink-0" />
        {CATEGORIES.map(c => {
          const n = c.key === 'ALL' ? records.length : records.filter(r => r.category === c.key).length;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                filter === c.key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {c.label}{n > 0 ? ` ${n}` : ''}
            </button>
          );
        })}
      </div>

      {notice && (
        <div className={`${notice.kind === 'ok' ? 'bg-teal-50 border-teal-200' : 'bg-red-50 border-red-200'} border rounded-xl p-3 flex items-start gap-2`}>
          <Upload className={`w-4 h-4 ${notice.kind === 'ok' ? 'text-teal-600' : 'text-red-500'} shrink-0 mt-0.5`} />
          <p className={`text-xs font-semibold ${notice.kind === 'ok' ? 'text-teal-800' : 'text-red-700'}`}>{notice.text}</p>
          <button onClick={() => setNotice(null)} className="text-gray-400 hover:text-gray-600 text-sm ml-auto shrink-0">✕</button>
        </div>
      )}

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Archive className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">
            {records.length === 0
              ? 'Nothing in your vault yet. Records appear here as your doctors, labs and pharmacies issue them.'
              : 'No documents in this category'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const v = VERIFICATION[r.verification] ?? { label: r.verification, fg: '#475569', bg: '#F8FAFC', border: '#E2E8F0', Icon: PenLine };
            const busy = busyId === r.id;
            const isUpload = isSelfUpload(r);
            return (
              <div key={`${r.source}-${r.id}`} className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900">{r.title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {[r.type, r.provider, fmtDate(r.date)].filter(Boolean).join(' · ')}
                    </p>
                    {r.documentNumber && (
                      <p className="text-[10px] text-gray-400 mt-0.5 font-mono">{r.documentNumber}</p>
                    )}
                  </div>
                  <span
                    className="text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1 shrink-0"
                    style={{ color: v.fg, background: v.bg, border: `1px solid ${v.border}` }}
                  >
                    <v.Icon className="w-3 h-3" /> {v.label}
                  </span>
                </div>

                <div className="mt-2.5 flex items-center gap-3 flex-wrap">
                  <button disabled={busy} onClick={() => handleDownload(r)}
                    className="text-[11px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 disabled:opacity-50">
                    <Download className="w-3 h-3" /> {busy ? 'Working…' : 'Download'}
                  </button>
                  <button
                    onClick={() => {
                      // The document's own history has a line for this and
                      // nothing ever wrote it.
                      if (r.neoVerifyId) void patientApi.logDocumentAccess(r.neoVerifyId, 'SHARE');
                      setShareItem({ id: r.neoVerifyId || r.id, title: r.title });
                    }}
                    className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                  {/* This document's own history, not the account-wide log. A
                      record with no registry entry has none to show. */}
                  {r.neoVerifyId && (
                    <button onClick={() => { void patientApi.logDocumentAccess(r.neoVerifyId!, 'VIEW'); setHistoryFor(r); }}
                      className="text-[11px] text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1">
                      <History className="w-3 h-3" /> History
                    </button>
                  )}
                  {/* Only what the patient put there themselves is theirs to
                      remove — a prescription is the doctor's record. */}
                  {isUpload && (
                    <button disabled={busy} onClick={() => handleDelete(r)}
                      className="text-[11px] text-red-500 hover:text-red-600 font-medium flex items-center gap-1 ml-auto disabled:opacity-50">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={GOV_COMPLIANCE.text}>NeoHealthCard™ · Documents are stored on your account and shared only with your consent</p>

      {shareItem && (
        <InstitutionShareModal
          open={!!shareItem}
          onClose={() => setShareItem(null)}
          certificateId={shareItem.id}
          certificateTitle={shareItem.title}
        />
      )}

      {historyFor && <DocumentHistoryDrawer record={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

/**
 * What has happened to ONE document — issued, registered, and every check,
 * download and share since.
 *
 * The drawer this replaces ignored which card was clicked and showed the same
 * account-wide access log every time, so "who opened this document" could not
 * be answered for any document.
 */
function DocumentHistoryDrawer({ record, onClose }: { readonly record: VaultRecord; readonly onClose: () => void }) {
  const [data, setData] = useState<VaultProvenance | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!record.neoVerifyId) return;
    let alive = true;
    setLoading(true);
    setFailed(false);
    patientApi.neoVerifyProvenance(record.neoVerifyId)
      .then((p) => { if (alive) setData(p); })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [record.neoVerifyId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button aria-label="Close" className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b border-gray-100 z-10">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900">Document history</h2>
            <p className="text-[11px] text-gray-500 truncate">{record.title}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        <div className="p-4 space-y-3">
          {record.neoVerifyId && (
            <p className="text-[10px] font-mono text-gray-400">{record.neoVerifyId}</p>
          )}
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          ) : failed ? (
            <p className="text-sm text-gray-500 py-8 text-center">This document&rsquo;s history could not be read.</p>
          ) : (
            (data?.stages ?? []).map((s) => (
              <div key={`${s.stage}-${s.at}`} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-800">{s.stage}</span>
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtWhen(s.at)}</span>
                </div>
                {s.detail && <p className="text-[11px] text-gray-500 mt-1">{s.detail}</p>}
                {/* Said plainly: the first line is the issuer's own date, which
                    predates this platform and nobody here witnessed. */}
                {!s.auditLogged && (
                  <p className="text-[10px] text-gray-400 mt-1">Recorded by the issuer, not audit-logged here</p>
                )}
              </div>
            ))
          )}
          {!loading && !failed && (data?.stages ?? []).length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">Nothing has happened to this document yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
